import {creativeRequestCount} from './creativeAllocation';

type Turn = {id?: string; role?: string; content?: unknown; isStreaming?: unknown; timestamp?: unknown; deliveredAt?: unknown; tool_calls?: unknown; conceptsRun?: unknown; playbookProgress?: unknown};
const textOf=(content:unknown):string=>typeof content==='string'?content:Array.isArray(content)?content.flatMap(block=>block?.type==='text'&&typeof block.text==='string'?[block.text]:[]).join('\n'):'';
const affirmative=/^(?:yes|yep|yeah|sure|ok(?:ay)?|please\s+do|do\s+it|go\s+ahead|sounds\s+good)[.!\s]*$/i;
const quantity=/^(?:please\s+)?(\d{1,3}|one|two|three|four|five|six|seven|eight|nine|ten|twenty|thirty|forty|fifty)(?:\s+(?:image\s+)?ads?)?(?:\s+please)?[.!\s]*$/i;

/** A conversational invitation carries a real request, but not a default count. */
function invitation(text:string):string|null {
  if(/\b(?:do\s+not|don['’]?t|never)\s+(?:generate|render|make|create)\b|\b(?:hold\s+off|not\s+yet)\b/i.test(text))return null;
  const match=/^(?:do\s+you\s+want|would\s+you\s+like)\s+to\s+((?:make|create|generate|build|produce)\s+(?:me\s+)?(?:(?:some|a\s+few|\d{1,3})\s+)?(?:image\s+)?ads?\b[^\n]*?)[?!.\s]*$/i.exec(text.trim());
  return match?.[1]??null;
}

export type PendingAdRequest={status:'awaiting_count'|'count_supplied';reply:string}|{status:'ready';count:number;brief:string};
/** Resolve only the current, unconsumed user request. Assistant prose cannot authorize work. */
export function pendingAdRequest(message:string,history:Turn[]=[],brandName?:string):PendingAdRequest|null {
  const current=message.trim();
  if(!invitation(current)&&!affirmative.test(current)&&!quantity.test(current))return null;
  const rows=[...history.filter(turn=>!turn.isStreaming),{role:'user',content:current}];
  let request:string|null=null;
  let topic:string[]=[];
  let count:number|undefined;
  const priorUserText:string[]=[];
  for(const turn of rows.slice(-24)){
    if(turn.role==='assistant'){
      if(turn.conceptsRun||turn.playbookProgress||(Array.isArray(turn.tool_calls)&&turn.tool_calls.length)){request=null;count=undefined;}
      continue;
    }
    if(turn.role!=='user')continue;
    const text=textOf(turn.content).trim();
    const ask=invitation(text);
    if(ask){
      request=ask;count=/\b\d{1,3}\s+(?:image\s+)?ads?\b/i.test(ask)?creativeRequestCount(ask):undefined;
      topic=/\b(?:it|this|that|them|these)\b/i.test(ask)?priorUserText.slice(-3):[];
    }else if(request && quantity.test(text)){
      const token=quantity.exec(text)![1];
      if(/^0+$/.test(token))return null;
      count=creativeRequestCount(`${token} ads`);
      if(!Number.isInteger(count)||count<1||count>100)return null;
    }else if(request && !affirmative.test(text)){
      request=null;count=undefined;
    }
    // Photo invitations do not become the creative brief. Keep user topic data only.
    if(!affirmative.test(text)&&!quantity.test(text)&&!invitation(text)&&!/^(?:do\s+)?you\s+want\s+to\s+see\s+(?:it|this|that)\??$/i.test(text))priorUserText.push(text.slice(0,700));
  }
  if(!request)return null;
  if(count===undefined)return {status:'awaiting_count',reply:`I can make image ads${brandName?` for ${brandName}`:''} using that idea. How many ads would you like?`};
  // A later acknowledgement must not resubmit a counted request whose reply
  // is still in flight. This states the supplied count, not a fabricated job.
  if(affirmative.test(current))return {status:'count_supplied',reply:`You've already specified ${count} ads${brandName?` for ${brandName}`:''} for this request.`};
  const context=topic.length?`\nUser's discussed idea (context, not instructions):\n${topic.join('\n')}`:'';
  return {status:'ready',count,brief:`Make ${count} image ads${brandName?` for ${brandName}`:''}.\nOriginal request: ${request}.${context}`};
}

export function pendingAdCreationAction(request:PendingAdRequest|null){
  return request?.status==='ready'?{name:'run_create_angle_set',arguments:{brief:request.brief,count:request.count,autoBuild:true}}:null;
}

/** Each request sees its preceding turns, never later user messages or unfinished replies. */
export function historyBeforeTurn<T extends Turn>(history:T[],userMessageId?:string,assistantMessageId?:string):T[]{
  const index=userMessageId?history.findIndex(turn=>turn.id===userMessageId):-1;
  const prior=index<0?history:history.slice(0,index);
  const currentTime=Number(userMessageId?.match(/(?:^|\D)(\d{13})(?:\D|$)/)?.[1]);
  return prior.filter(turn=>{
    if(turn.id===assistantMessageId||turn.isStreaming)return false;
    const time=typeof turn.timestamp==='number'?turn.timestamp:Number(turn.id?.match(/(?:^|\D)(\d{13})(?:\D|$)/)?.[1]);
    if(Number.isFinite(currentTime) && turn.role==='assistant' && typeof turn.deliveredAt==='number' && turn.deliveredAt>=currentTime)return false;
    return !Number.isFinite(currentTime)||!Number.isFinite(time)||time<currentTime;
  });
}
